import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DashboardMapping } from '../../../UserComponents'
import Authentication from '../../../libs/api/services/Authentication'
import Dashboards from '../../../libs/api/services/Dashboards'
import { ClaimsFilter } from '../../../libs/authentication/ClaimsFilter'
import CommonFunctionsContext from '../../common/CommonFunctionsProvider'
import DashboardLayouts from './DashboardLayouts'
import { DashboardProvider } from './Widgets/DashboardProvider'
import DashboardWidgetAlert from './Widgets/DashboardWidgetAlert'
import DashboardWidgetCalendar from './Widgets/DashboardWidgetCalendar'
import DashboardWidgetConsent from './Widgets/DashboardWidgetConsent'
import DashboardWidgetMessages from './Widgets/DashboardWidgetMessages'
import DashboardWidgetProfile from './Widgets/DashboardWidgetProfile'
import DashboardWidgetQuestionnaires from './Widgets/DashboardWidgetQuestionnaires'
import DashboardWidgetToDo from './Widgets/DashboardWidgetToDo'
import DashboardWorkFlowCard from './Widgets/DashboardWorkFlowCard'
import './index.css'
// Widget configuration with proper error boundaries
const WIDGET_CONFIGS = {
    Questionnaires: {
        component: DashboardWidgetQuestionnaires,
        resources: [
            { resource: "Questionnaires", type: 'View' },
            { resource: "Questionnaires", type: 'Edit' },
            { resource: "Questionnaires", type: 'Inherit' }
        ]
    },
    Profile: {
        component: DashboardWidgetProfile,
        resources: []
    },
    Consent: {
        component: DashboardWidgetConsent,
        resources: []
    },
    Alerts: {
        component: DashboardWidgetAlert,
        resources: [
            { resource: "Alerts", type: 'View' },
            { resource: "Alerts", type: 'Edit' },
            { resource: "Alerts", type: 'Inherit' }
        ]
    },
    Messages: {
        component: DashboardWidgetMessages,
        resources: [
            { resource: "Messages|Own", type: 'View' },
            { resource: "Messages|Own", type: 'Edit' },
            { resource: "Messages|Own", type: 'Inherit' }
        ]
    },
    Calendar: {
        component: DashboardWidgetCalendar,
        resources: [
            { resource: "Calendars", type: 'View' },
            { resource: "Calendars", type: 'Edit' },
            { resource: "Calendars", type: 'Inherit' }
        ]
    },
    ToDo: {
        component: DashboardWidgetToDo,
        resources: [
            { resource: "To Do Lists", type: 'View' },
            { resource: "To Do Lists", type: 'Edit' },
            { resource: "To Do Lists", type: 'Inherit' }
        ]
    }
}

export default function Dashboard({ type, test }) {
    const { setAssistantSection } = useContext(CommonFunctionsContext)
    const [dt, setDT] = useState(test)
    const [workFlowCards, setWorkFlowCards] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Memoize user roles to avoid repeated authentication calls
    const userRoles = useMemo(() => {
        try {
            return Authentication.getFacilityRoles() || []
        } catch (err) {
            console.error('Failed to get facility roles:', err)
            return []
        }
    }, [])

    // Initialize dashboard type
    useEffect(() => {
        setAssistantSection('Dashboard')
        try {
            if (type) {
                setDT(type)
            } else {
                const facilityRole = Authentication.getFacilityRole()
                if (facilityRole && DashboardMapping[facilityRole]?.main) {
                    setDT(DashboardMapping[facilityRole].main)
                } else {
                    console.warn('No dashboard mapping found for facility role:', facilityRole)
                }
            }
        } catch (err) {
            console.error('Error initializing dashboard:', err)
            setError('Failed to initialize dashboard')
        }
    }, [type, setAssistantSection])

    // Load workflow cards with proper error handling
    const loadWorkFlowCards = useCallback(async () => {
        if (!userRoles.length) {
            setWorkFlowCards([])
            return
        }

        try {
            setLoading(true)
            const cards = []

            if (userRoles.includes('Admin')) {
                try {
                    const adminCards = await Dashboards.GetDashboardWorkflowCards('ADMIN_DASHBOARD')
                    if (Array.isArray(adminCards)) {
                        adminCards.forEach((card, index) => {
                            cards.push(
                                <DashboardWorkFlowCard
                                    key={`admin-${index}`}
                                    {...card}
                                />
                            )
                        })
                    }
                } catch (err) {
                    console.error('Failed to load admin workflow cards:', err)
                }
            }

            if (userRoles.includes('Clinician')) {
                try {
                    const clinicianCards = await Dashboards.GetDashboardWorkflowCards('CLINICIAN_DASHBOARD')
                    if (Array.isArray(clinicianCards)) {
                        clinicianCards.forEach((card, index) => {
                            cards.push(
                                <DashboardWorkFlowCard
                                    key={`clinician-${index}`}
                                    {...card}
                                />
                            )
                        })
                    }
                } catch (err) {
                    console.error('Failed to load clinician workflow cards:', err)
                }
            }

            setWorkFlowCards(cards)
        } catch (err) {
            console.error('Error loading workflow cards:', err)
            setError('Failed to load workflow cards')
        } finally {
            setLoading(false)
        }
    }, [userRoles])

    useEffect(() => {
        loadWorkFlowCards()
    }, [loadWorkFlowCards])

    // Create widget component with proper error handling
    const createWidget = useCallback((widgetType, index) => {
        const config = WIDGET_CONFIGS[widgetType]
        if (!config) {
            console.warn(`Unknown widget type: ${widgetType}`)
            return null
        }

        const WidgetComponent = config.component
        const key = `${widgetType}-${index}`

        try {
            if (config.resources.length > 0) {
                return (
                    <ClaimsFilter
                        key={key}
                        resources={config.resources}
                        claim={[]}
                    >
                        <WidgetComponent />
                    </ClaimsFilter>
                )
            } else {
                return <WidgetComponent key={key} />
            }
        } catch (err) {
            console.error(`Error creating widget ${widgetType}:`, err)
            return (
                <div key={key} className="alert alert-warning">
                    Failed to load {widgetType} widget
                </div>
            )
        }
    }, [])

    // Check if user has permission for widget
    const hasPermission = useCallback((widget) => {
        if (!widget.permissions || !Array.isArray(widget.permissions)) {
            return true // Default to showing if no permissions specified
        }

        return userRoles.some(role => widget.permissions.includes(role))
    }, [userRoles])

    // Get dashboard cards based on type
    const getDashboardCards = useMemo(() => {
        if (!dt || !DashboardLayouts) {
            return []
        }

        try {
            if (type === 'HOME') {
                const homeLayout = DashboardLayouts['HOME']
                if (!homeLayout?.widgets) {
                    console.warn('HOME layout not found or has no widgets')
                    return []
                }

                return homeLayout.widgets
                    .filter(hasPermission)
                    .map((widget, index) => createWidget(widget.type, index))
                    .filter(Boolean)
            } else {
                const layout = DashboardLayouts[dt]
                if (!layout?.widgets) {
                    console.warn(`Layout ${dt} not found or has no widgets`)
                    return []
                }

                return layout.widgets
                    .map((widget, index) => createWidget(widget.type, index))
                    .filter(Boolean)
            }
        } catch (err) {
            console.error('Error generating dashboard cards:', err)
            setError('Failed to load dashboard widgets')
            return []
        }
    }, [dt, type, hasPermission, createWidget])

    // Error state
    if (error) {
        return (
            <div className="m-3 dashboard-container h-100">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Dashboard Error</h4>
                    <p>{error}</p>
                    <button
                        className="btn btn-outline-danger"
                        onClick={() => {
                            setError(null)
                            loadWorkFlowCards()
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    // Loading state
    if (loading && !dt) {
        return (
            <div className="m-3 dashboard-container h-100 d-flex justify-content-center align-items-center">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    // Main render
    if (!dt) {
        return (
            <div className="m-3 dashboard-container h-100">
                <div className="alert alert-info" role="alert">
                    No dashboard configuration available for your role.
                </div>
            </div>
        )
    }

    return (
        <DashboardProvider>
            <div className='m-3 dashboard-container h-100'>
                <div className='row gy-2 gx-2 sm:d-none dashboard-row h-100'>
                    {getDashboardCards}
                    {workFlowCards}
                </div>
            </div>
        </DashboardProvider>
    )
}
